import type { FieldPath, FieldValues } from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Password } from "@/components/password";
import { getFieldCopy } from "@/i18n";
import type { CdcFieldBaseProps, CdcFieldCopy } from "./cdc-field-helpers";

export type CdcPasswordFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = CdcFieldBaseProps<TFieldValues, TName>;

export function CdcPasswordField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  locale,
  name,
}: Readonly<CdcPasswordFieldProps<TFieldValues, TName>>) {
  const copy = getFieldCopy(locale, "password") as unknown as CdcFieldCopy;
  const requiredLabel = copy.errors?.required ?? "Required";

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <div>
            <FormLabel className="inline-block leading-snug">
              {copy.label}
              <span className="ms-1 text-red-500" aria-hidden="true">
                *
              </span>
              <span className="sr-only">({requiredLabel})</span>
            </FormLabel>
            {copy.description ? (
              <FormDescription>{copy.description}</FormDescription>
            ) : null}
          </div>
          <FormControl>
            <Password
              aria-required="true"
              autoComplete="current-password"
              name={field.name}
              value={(field.value as string) ?? ""}
              onBlur={field.onBlur}
              onChange={field.onChange}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
